import { useEffect, useState } from "react";
import { api } from "./api";
import { Btn, Field, TextInput, Toggle, useSaveState } from "./atoms";

/* ============================================================
   /admin/scripts — Custom Scripts Manager
   Third-party tracking / pixels / chat / etc. loaded globally.
============================================================ */

const PLACEMENTS = [
  { value: "head", label: "Head", hint: "Inside <head>. Best for analytics, tag managers, preconnect hints." },
  { value: "body_start", label: "Start of <body>", hint: "First child of <body>. Use for GTM noscript fallbacks." },
  { value: "body_end", label: "End of <body>", hint: "Last child of <body>. Best for chat widgets, late-loading SDKs." },
];

const EMPTY_FORM = { name: "", code: "", placement: "head", enabled: false };

const ScriptCard = ({ script, onPatch, onDelete }) => {
  const [draft, setDraft] = useState(script);
  const [editing, setEditing] = useState(false);
  const { saving, saved, error, wrap } = useSaveState();

  useEffect(() => setDraft(script), [script]);
  const dirty =
    draft.name !== script.name ||
    draft.code !== script.code ||
    draft.placement !== script.placement ||
    draft.enabled !== script.enabled;

  const onSave = () => wrap(() => onPatch(script.id, draft).then(() => setEditing(false)));
  const onToggleEnabled = (v) => wrap(() => onPatch(script.id, { enabled: v }));

  return (
    <div
      data-testid={`script-${script.id}`}
      className="rounded-xl border border-white/[0.08] bg-[#0F141A]"
    >
      <div className="flex items-start gap-4 px-5 py-4 border-b border-white/[0.06]">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className={`inline-flex items-center gap-1.5 text-[0.6rem] font-bold tracking-[0.22em] uppercase px-2 py-1 rounded ${
                script.enabled
                  ? "bg-[#F4D64E]/[0.15] text-[#F4D64E]"
                  : "bg-white/[0.05] text-neutral-500"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${script.enabled ? "bg-[#F4D64E]" : "bg-neutral-600"}`}
              />
              {script.enabled ? "Live" : "Disabled"}
            </span>
            <span className="text-[0.6rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
              {(PLACEMENTS.find((p) => p.value === script.placement) || {}).label || script.placement}
            </span>
          </div>
          <div className="mt-2 font-display text-[1.05rem] font-bold tracking-[-0.01em] text-white truncate">
            {script.name || "Untitled script"}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Toggle value={!!script.enabled} onChange={onToggleEnabled} label="" />
          <Btn variant="ghost" onClick={() => setEditing(!editing)}>
            {editing ? "Close" : "Edit"}
          </Btn>
          <Btn
            variant="danger"
            onClick={() => {
              if (window.confirm(`Delete "${script.name}"? This cannot be undone.`)) {
                onDelete(script.id);
              }
            }}
          >
            ×
          </Btn>
        </div>
      </div>

      {editing ? (
        <div className="p-5 space-y-5">
          <Field label="Script name" hint="Internal label — not shown on the site.">
            <TextInput value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} />
          </Field>
          <Field label="Placement">
            <select
              value={draft.placement}
              onChange={(e) => setDraft({ ...draft, placement: e.target.value })}
              className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white"
            >
              {PLACEMENTS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <div className="mt-2 text-xs text-neutral-500">
              {(PLACEMENTS.find((p) => p.value === draft.placement) || {}).hint}
            </div>
          </Field>
          <Field
            label="Script code"
            hint="Paste the full embed snippet. Can include <script> tags, noscript pixels, or inline JS."
          >
            <textarea
              value={draft.code || ""}
              onChange={(e) => setDraft({ ...draft, code: e.target.value })}
              rows={10}
              placeholder={`<!-- Example: Google tag manager -->\n<script>(function(w,d,s,l,i){ ... })(window,document,'script','dataLayer','GTM-XXXXXX');</script>`}
              className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-xs text-white focus:outline-none focus:border-[#F4D64E]/60 transition-colors font-mono leading-[1.55]"
            />
          </Field>
          <div className="flex items-center gap-3">
            <Btn onClick={onSave} disabled={!dirty || saving}>
              {saving ? "Saving…" : "Save changes"}
            </Btn>
            {saved ? <span className="text-[0.7rem] text-[#F4D64E]">Saved ✓</span> : null}
            {error ? <span className="text-[0.7rem] text-red-400">{error}</span> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default function ScriptsPage() {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(EMPTY_FORM);
  const { saving, error, wrap } = useSaveState();

  const load = () => api.scriptsList().then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);

  const onCreate = () =>
    wrap(async () => {
      const name = (draft.name || "").trim();
      if (!name) throw new Error("Name is required.");
      await api.scriptCreate(draft);
      setDraft(EMPTY_FORM);
      setCreating(false);
      await load();
    });

  const onPatch = async (id, body) => {
    await api.scriptUpdate(id, body);
    await load();
  };
  const onDelete = async (id) => {
    await api.scriptDelete(id);
    await load();
  };

  if (items === null) {
    return <div className="text-sm text-neutral-500">Loading…</div>;
  }

  return (
    <div data-testid="admin-scripts-page" className="pb-20">
      <div className="flex items-start justify-between gap-4 mb-9">
        <div>
          <h1 className="font-display text-3xl font-black tracking-[-0.025em]">
            Custom scripts
          </h1>
          <p className="mt-2 text-sm text-neutral-400 max-w-2xl">
            Third-party tracking, pixels, chat widgets, or any embed that needs to load
            across the public site. Each script is injected at the selected placement on
            every page. Disabled scripts are never injected.
          </p>
        </div>
        {!creating ? (
          <Btn onClick={() => setCreating(true)} data-testid="add-script-btn">
            ＋ Add script
          </Btn>
        ) : null}
      </div>

      {creating ? (
        <div
          data-testid="new-script-form"
          className="mb-8 rounded-xl border border-[#F4D64E]/25 bg-[#1a1505] p-5 space-y-5"
        >
          <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
            New script
          </div>
          <Field label="Script name" hint="Internal label — not shown on the site.">
            <TextInput
              value={draft.name}
              onChange={(v) => setDraft({ ...draft, name: v })}
              placeholder="Google Tag Manager"
              data-testid="new-script-name"
            />
          </Field>
          <Field label="Placement">
            <select
              value={draft.placement}
              onChange={(e) => setDraft({ ...draft, placement: e.target.value })}
              data-testid="new-script-placement"
              className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white"
            >
              {PLACEMENTS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <div className="mt-2 text-xs text-neutral-500">
              {(PLACEMENTS.find((p) => p.value === draft.placement) || {}).hint}
            </div>
          </Field>
          <Field label="Script code" hint="Paste the full embed snippet.">
            <textarea
              value={draft.code || ""}
              onChange={(e) => setDraft({ ...draft, code: e.target.value })}
              rows={8}
              data-testid="new-script-code"
              placeholder="<script>...</script>"
              className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-xs text-white focus:outline-none focus:border-[#F4D64E]/60 transition-colors font-mono leading-[1.55]"
            />
          </Field>
          <Toggle
            value={draft.enabled}
            onChange={(v) => setDraft({ ...draft, enabled: v })}
            label="Enable immediately after save"
          />
          <div className="flex items-center gap-3">
            <Btn onClick={onCreate} disabled={saving} data-testid="save-script-btn">
              {saving ? "Saving…" : "Save script"}
            </Btn>
            <Btn
              variant="ghost"
              onClick={() => {
                setCreating(false);
                setDraft(EMPTY_FORM);
              }}
            >
              Cancel
            </Btn>
            {error ? <span className="text-[0.7rem] text-red-400">{error}</span> : null}
          </div>
        </div>
      ) : null}

      {items.length === 0 && !creating ? (
        <div className="rounded-xl border border-dashed border-white/[0.10] p-10 text-center">
          <div className="font-display text-[1.25rem] font-bold text-white mb-2">
            No scripts yet.
          </div>
          <p className="text-sm text-neutral-400 max-w-md mx-auto">
            Add your first script to start loading tracking or embedded widgets across
            the site. Scripts stay disabled until you toggle them on.
          </p>
        </div>
      ) : null}

      <div className="space-y-4">
        {items.map((s) => (
          <ScriptCard key={s.id} script={s} onPatch={onPatch} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}
